import { useQuery } from "@tanstack/react-query";
import { request } from "./client";

/**
 * Boshqaruv paneli agregatlari (reporting-service).
 *
 * Panel ilgari `tasks` hisobotini `limit=200` bilan tortib,
 * qatorlarini SANARDI. Ombor kuniga ikki yuzdan ortiq topshiriq
 * bajarsa, karta chegarada to'xtab qolar va yolg'on son
 * ko'rsatardi — dispetcher esa aynan shu songa qarab odam
 * taqsimlaydi. Endi sanoq bazada bajariladi.
 */

/** Hozir ochiq topshiriq: tur × holat. Davrga BOG'LIQ EMAS. */
export interface OpenCount {
  task_type: string;
  status: string;
  count: number;
  /**
   * Topshiriq satrlari soni — interfeysda «SKU».
   *
   * DIQQAT: turli tovarlar soni EMAS. Bir tovar bir nechta
   * topshiriqda uchrasa, har birida qayta sanaladi.
   */
  lines: number;
  /** Bajarilishi kerak bo'lgan miqdor (MATN). */
  planned_qty: string;
}

/** Davr ichidagi harakat. */
export interface PeriodCount {
  task_type: string;
  created: number;
  completed: number;
  cancelled: number;
  work_seconds: number;
  /** Yakunlanganlarning satrlari — interfeysda «SKU». */
  lines: number;
  /** Yakunlanganlarning FAKT miqdori (MATN). */
  actual_qty: string;
}

export interface PodiumRow {
  employee_id: string;
  task_count: number;
  line_count: number;
  /** MATN: JSON float yaxlitlashi miqdorni buzardi. */
  actual_qty: string;
  work_seconds: number;
  shortage_count: number;
  penalty_count: number;
}

/**
 * Solishtirish davri.
 *
 * Faqat DAVR kesimi: «hozir ochiq» va «hozir band» holatlari
 * o'tmish uchun saqlanmaydi va ularni solishtirib bo'lmaydi.
 */
export interface ComparePeriod {
  from: string;
  to: string;
  period: PeriodCount[];
}

/**
 * Topshiriq turi bo'yicha eng natijali xodim.
 *
 * Tur bo'yicha, ROL bo'yicha emas: rol xodim kartasida turadi va
 * bir nechta bo'lishi mumkin (teruvchi + katta xodim), KPI esa
 * BAJARILGAN ish bo'yicha yig'iladi. Ombor oqimida tur bilan rol
 * birma-bir mos keladi, shuning uchun natija «eng yaxshi
 * teruvchi» savoliga javob beradi.
 */
export interface LeaderRow extends PodiumRow {
  task_type: string;
  /** Tur ichidagi o'rin (1 — birinchi). */
  rank: number;
}

export interface DashboardSummary {
  from: string;
  /** Chegaradan TASHQARI. */
  to: string;
  open: OpenCount[];
  period: PeriodCount[];
  busy_employee_ids: string[];
  podium: PodiumRow[];
  leaders: LeaderRow[];
  compare?: ComparePeriod;
}

/** Bitta kesimdagi qoldiq va sig'im (foizsiz — izoh serverda). */
export interface FillRow {
  key: string;
  warehouse_code?: string;
  qty: string;
  lp_count: number;
  sku_count: number;
  capacity_slots: number;
  location_count: number;
}

export interface WarehouseFill {
  functions: FillRow[];
  zones: FillRow[];
  abc_zones: FillRow[];
}

/**
 * Ombor to'ldirilishi.
 *
 * Davrga BOG'LIQ EMAS: bu hozirgi qoldiq holati, tarix emas.
 * Shuning uchun davr tanlagich bu so'rovga ta'sir qilmaydi.
 */
export function useWarehouseFill() {
  return useQuery({
    queryKey: ["report", "warehouse-fill"],
    queryFn: ({ signal }) =>
      request<WarehouseFill>("reporting", "/api/v1/warehouse-fill", { signal }),
  });
}

/** Holat bo'yicha hujjatlar soni (sanoq bazada bajariladi). */
export interface StatusCount {
  status: string;
  count: number;
}

/**
 * Davr oralig'i — `YYYY-MM-DD` ko'rinishida.
 *
 * Sana MATN sifatida saqlanadi: `Date` obyekti soat bilan birga
 * keladi va so'rov kaliti har daqiqada o'zgarib, keshni
 * foydasiz qilardi.
 */
export interface DateRange {
  from: string;
  to: string;
}

/** Bugundan orqaga `days` kunlik oraliq (bugun ham kiradi). */
export function lastDays(days: number): DateRange {
  const to = new Date();
  const from = new Date();
  from.setDate(from.getDate() - (days - 1));
  return { from: isoDate(from), to: isoDate(to) };
}

/** Oralig'i bir xil, lekin undan OLDIN turgan davr. */
export function previousRange(range: DateRange): DateRange {
  const from = new Date(`${range.from}T00:00:00`);
  const to = new Date(`${range.to}T00:00:00`);
  const days = Math.round((to.getTime() - from.getTime()) / 86_400_000) + 1;
  const prevTo = new Date(from);
  prevTo.setDate(prevTo.getDate() - 1);
  const prevFrom = new Date(prevTo);
  prevFrom.setDate(prevFrom.getDate() - (days - 1));
  return { from: isoDate(prevFrom), to: isoDate(prevTo) };
}

/** Oraliqdagi kunlar soni (ikkala chegara ham kiradi). */
export function rangeDays(range: DateRange): number {
  const from = new Date(`${range.from}T00:00:00`);
  const to = new Date(`${range.to}T00:00:00`);
  return Math.round((to.getTime() - from.getTime()) / 86_400_000) + 1;
}

export function isoDate(date: Date): string {
  // `toISOString` UTC ga o'tkazadi va mahalliy mintaqada kun
  // chegarasini SURIB yuborardi — kechqurun tanlangan «bugun»
  // ertangi sana bo'lib ketardi.
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${date.getFullYear()}-${month}-${day}`;
}

export function useDashboard(
  range: DateRange,
  compare: DateRange | null,
  options: { enabled?: boolean } = {},
) {
  const query: Record<string, string> = {
    // Server sanani kun chegarasiga keltiradi, shuning uchun soat
    // yubormaymiz: so'rov kaliti kun davomida o'zgarmaydi va
    // kesh har daqiqada bekor bo'lmaydi.
    from: `${range.from}T00:00:00Z`,
    to: `${range.to}T00:00:00Z`,
  };
  if (compare) {
    query.compare_from = `${compare.from}T00:00:00Z`;
    query.compare_to = `${compare.to}T00:00:00Z`;
  }

  return useQuery({
    // Kalit `report` dan boshlanadi: jonli oqim (SSE) proyeksiya
    // o'zgarganda aynan shu prefiksni bekor qiladi, demak panel
    // o'zi uchun alohida yangilanish mexanizmini talab qilmaydi.
    queryKey: ["report", "dashboard", range.from, range.to, compare?.from, compare?.to],
    enabled: options.enabled ?? true,
    queryFn: ({ signal }) =>
      request<DashboardSummary>("reporting", "/api/v1/dashboard", { query, signal }),
  });
}
